import { Link } from "react-router-dom";
import { AlertOctagon, Package, Clock, ChevronRight, ArrowRight } from "lucide-react";

const statusMap = {
  open: { label: "Received", chip: "bg-[#a8dadc]/40 text-[#457b9d]" },
  in_progress: { label: "In Progress", chip: "bg-[#457b9d]/15 text-[#457b9d]" },
  awaiting_approval: { label: "Awaiting Approval", chip: "bg-[#F79009]/15 text-[#F79009]" },
  awaiting_parts: { label: "Waiting for Parts", chip: "bg-[#e63946]/10 text-[#e63946]" },
  completed: { label: "Ready", chip: "bg-[#12B76A]/15 text-[#12B76A]" },
  delivered: { label: "Delivered", chip: "bg-[#1d3557]/10 text-[#1d3557]" },
};

const fmtETA = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? `Today, ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`
    : d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
};

/**
 * ActiveJobsTable — the primary operational list on the dashboard.
 *
 * Columns (desktop): Job Card, Vehicle, Customer, Status, Assigned To, Parts,
 * Expected Delivery, Action.  Mobile falls back to stacked cards.
 * Delayed rows get a left red indicator.
 */
export function ActiveJobsTable({ rows = [] }) {
  return (
    <section className="bg-white rounded-xl border border-[#E4E7EC]" data-testid="active-jobs-table">
      <header className="px-5 py-3.5 border-b border-[#F1FAEE] flex items-center justify-between">
        <div>
          <h3 className="text-[15px] font-semibold text-[#1d3557]">Active Job Cards</h3>
          <p className="text-[11.5px] text-[#667085] mt-0.5">
            Delayed and waiting-for-parts jobs are listed first.
          </p>
        </div>
        <Link
          to="/job-cards"
          data-testid="active-jobs-view-all"
          className="text-[12px] font-semibold text-[#457b9d] hover:text-[#e63946] inline-flex items-center gap-1"
        >
          View all job cards <ArrowRight size={13} />
        </Link>
      </header>

      {rows.length === 0 ? (
        <div className="p-12 text-center">
          <div className="text-[13px] font-semibold text-[#1d3557]">No active job cards</div>
          <p className="text-[11.5px] text-[#667085] mt-1">
            Create a job card when the next vehicle arrives.
          </p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="active-jobs-desktop">
              <thead className="text-left">
                <tr className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] border-b border-[#F1FAEE]">
                  <th className="px-5 py-2.5 w-[110px]">Job Card</th>
                  <th className="px-3 py-2.5">Vehicle</th>
                  <th className="px-3 py-2.5">Customer</th>
                  <th className="px-3 py-2.5">Status</th>
                  <th className="px-3 py-2.5">Assigned</th>
                  <th className="px-3 py-2.5">Parts</th>
                  <th className="px-3 py-2.5">Delivery</th>
                  <th className="px-5 py-2.5 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const status = statusMap[r.status] || { label: r.status, chip: "bg-[#66708515] text-[#667085]" };
                  return (
                    <tr
                      key={r.id}
                      className={`border-b border-[#F1FAEE] hover:bg-[#F7F9FC] cursor-pointer ${
                        r.is_delayed ? "border-l-2 border-l-[#e63946]" : ""
                      }`}
                      onClick={() => (window.location.href = `/job-cards/${r.id}`)}
                      data-testid={`active-jobs-row-${r.job_number}`}
                    >
                      <td className="px-5 py-3">
                        <div className="font-mono text-[12.5px] font-semibold text-[#1d3557]">
                          {r.job_number}
                        </div>
                        {r.is_delayed && (
                          <div className="text-[10px] text-[#e63946] font-semibold mt-0.5 flex items-center gap-1">
                            <AlertOctagon size={10} /> Delayed
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="font-medium text-[#1d3557]">
                          {r.vehicle_reg || "—"}
                        </div>
                        <div className="text-[11px] text-[#667085] truncate max-w-[130px]">
                          {r.vehicle_make} {r.vehicle_model}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-[#1d3557] truncate max-w-[140px]">
                        {r.customer_name || "—"}
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider ${status.chip}`}
                        >
                          {status.label}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-[12.5px] text-[#1d3557]">
                        {r.assigned_to_name || <span className="text-[#667085]">Unassigned</span>}
                      </td>
                      <td className="px-3 py-3">
                        {r.waiting_for_parts ? (
                          <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#e63946]">
                            <Package size={11} /> Waiting
                          </span>
                        ) : (
                          <span className="text-[12px] text-[#12B76A]">Available</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`inline-flex items-center gap-1 text-[12px] ${
                            r.is_delayed ? "text-[#e63946] font-semibold" : "text-[#667085]"
                          }`}
                        >
                          <Clock size={11} /> {fmtETA(r.promised_at)}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <Link
                          to={`/job-cards/${r.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 h-7 px-3 rounded-md border border-[#E4E7EC] text-[11.5px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE]"
                        >
                          View <ChevronRight size={11} />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-[#F1FAEE]" data-testid="active-jobs-mobile">
            {rows.map((r) => {
              const status = statusMap[r.status] || { label: r.status, chip: "bg-[#66708515] text-[#667085]" };
              return (
                <Link
                  key={r.id}
                  to={`/job-cards/${r.id}`}
                  data-testid={`active-jobs-card-${r.job_number}`}
                  className={`block p-4 hover:bg-[#F7F9FC] ${
                    r.is_delayed ? "border-l-2 border-l-[#e63946]" : ""
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="font-mono text-[13px] font-semibold text-[#1d3557]">
                      {r.job_number}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-[9.5px] font-semibold uppercase tracking-wider ${status.chip}`}>
                      {status.label}
                    </span>
                    {r.is_delayed && (
                      <span className="text-[9.5px] font-semibold text-[#e63946]">DELAYED</span>
                    )}
                  </div>
                  <div className="text-[13px] font-medium text-[#1d3557]">
                    {r.vehicle_reg || "—"} · {r.customer_name || "—"}
                  </div>
                  <div className="text-[11.5px] text-[#667085] mt-1 flex items-center gap-3">
                    <span className="flex items-center gap-1">
                      <Clock size={11} /> {fmtETA(r.promised_at)}
                    </span>
                    <span>{r.assigned_to_name || "Unassigned"}</span>
                    {r.waiting_for_parts && (
                      <span className="text-[#e63946] font-semibold">Parts pending</span>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}
